import debounce from 'lodash/debounce';
import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import BookShipmentModal from 'src/components/BookShipmentModal';
import LoadingIndicator from 'src/components/LoadingIndicator';
import MapHeaderQuote from 'src/components/MapHeaderQuote';
import { Amount } from 'src/components/QuoteBanner';
import RatePicker from 'src/components/RatePicker';
import { QuoteUnits } from 'src/components/ShipmentQuotes';
import emptyImg from 'src/images/empty.svg';
import { useAppState } from 'src/lib/hooks';
import {
  AddressCardPreview,
  Button,
  ButtonGroup,
  Calendar,
  Card,
  CardBody,
  Col,
  Container,
  Form,
  FormFeedback,
  FormGroup,
  Formik,
  formik,
  FormInput,
  Icon,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  Label,
  Link,
  ListGroup,
  ListGroupItem,
  MapImage,
  MarkerImage,
  Modal,
  ModalBody,
  ModalFooter,
  Nav,
  NavItem,
  NavLink,
  Progress,
  Row,
  TabContent,
  Table,
  TableBody,
  TableData,
  TableHead,
  TableHeader,
  TableRow,
  TabPane,
  toast,
  UncontrolledTooltip,
  useApp,
  useScreenSize,
  useStateIn
} from 'straightline-ui';
import { formatMoney, formatNumber } from 'straightline-utils/accounting';
import {
  ACCESSORIAL_CODES,
  estimateDueDate,
  FREIGHT_CLASSES,
  isAccepted,
  isExpired,
  isFTL,
  isLTL,
  isParcel,
  LOADSURE,
  MODE,
  MODES,
  PACKAGE_TYPES,
  TRUCK_TYPE,
  TRUCK_TYPES
} from 'straightline-utils/constants';
import { delIn, getIn, pushIn, setIn } from 'straightline-utils/immutable';
import {
  addDays,
  addHours,
  formatDate,
  formatDistanceToNowStrict,
  fromZonedTime,
  isSameDay,
  isWeekend,
  parseDate,
  parseTimezone,
  startOfDay,
  toZonedTime
} from 'straightline-utils/time';
import { getFreightClass } from 'straightline-utils/validation';
import { addressSchema } from 'straightline-utils/validation/schemas';

const { LTL, FTL, PARCEL } = MODE;

const PARCEL_CODES = {
  PICKUP: [{ value: 'RESPU', label: 'Residential' }],
  DELIVERY: [{ value: 'RESDEL', label: 'Residential' }]
};

const FTL_CODES = {
  PICKUP: ACCESSORIAL_CODES.PICKUP.filter((code) => code.value !== 'INPU'),
  DELIVERY: ACCESSORIAL_CODES.DELIVERY.filter((code) => code.value !== 'INDEL')
};

const LTL_CODES = {
  PICKUP: ACCESSORIAL_CODES.PICKUP.filter((code) => code.value !== 'DVRPU'),
  DELIVERY: ACCESSORIAL_CODES.DELIVERY.filter((code) => code.value !== 'DVRDEL')
};

const PARCEL_PACKAGE_TYPES = [
  { value: 'BOX', label: 'Box' },
  { value: 'ENVELOPE', label: 'Envelope' },
  { value: 'BAG', label: 'Bag' },
  { value: 'TUBE', label: 'Tube' }
];

const tabStyle = {
  marginTop: -36,
  marginLeft: -1,
  borderBottomWidth: 0,
  borderBottomLeftRadius: 0,
  borderBottomRightRadius: 0,
  width: 315
};

const initialLineItems = (hasShipperAddress) => {
  const item = {
    type: 'PLT',
    description: undefined,
    units: undefined,
    length: undefined,
    width: undefined,
    height: undefined,
    total_weight: undefined,
    stack_limit: 1,
    freight_class: undefined,
    insurance: null
  };

  if (hasShipperAddress) {
    item.insurance = {
      amount: undefined,
      commodity: undefined
    };
  }

  return [item];
};

const commodityOptions = LOADSURE.COMMODITIES.map((comm) => {
  return {
    label: comm.label,
    value: comm.label
  };
});

const ENABLED_TRUCK_TYPES = TRUCK_TYPES.filter((truck) => truck.enabled);

const ENABLED_MODES = MODES.filter((mode) => mode.enabled).map((mode) => {
  if (mode.label === MODE.FTL) {
    return {
      ...mode,
      label: 'Full Truckload'
    };
  }
  if (mode.label === MODE.LTL) {
    return {
      ...mode,
      label: 'Less Than Truckload'
    };
  }

  return mode;
});

function getInitialValues(copiedData, defaultAddresses, hasShipperAddress) {
  if (!copiedData) {
    const defaultPickup = defaultAddresses?.data?.find(
      (detail) => detail.default_address === 'pickup'
    );
    const defaultDelivery = defaultAddresses?.data?.find(
      (detail) => detail.default_address === 'delivery'
    );

    const pickup_address = defaultPickup
      ? {
          ...defaultPickup.address,
          appointment_confirmed: false
        }
      : undefined;

    const delivery_address = defaultDelivery
      ? {
          ...defaultDelivery.address,
          appointment_confirmed: false
        }
      : undefined;

    return {
      // pickup_date: formatDate(addDays(new Date(), 1), 'dateOnly'),
      // pickup_date: formatDate(addDays(new Date(), 1), 'dateOnly'),
      pickup_date: undefined,
      due_date: undefined,
      truck_type: TRUCK_TYPE.DRY_VAN,
      load_weight: undefined,
      mode: FTL,
      pickup_address,
      delivery_address,
      line_items: initialLineItems(hasShipperAddress),
      accessorial_codes: undefined
    };
  }

  const isPastDate =
    new Date(copiedData.pickup_date) <
    new Date(formatDate(new Date(), 'dateOnly'));

  // let accessorial_codes = undefined;
  // if (copiedData.accessorial_codes) {
  //   accessorial_codes = copiedData.accessorial_codes.filter((code) => {
  //     return (
  //       ACCESSORIAL_CODES.PICKUP.find(({ value }) => value === code) ||
  //       ACCESSORIAL_CODES.DELIVERY.find(({ value }) => value === code)
  //     );
  //   });
  //   if (!accessorial_codes.length) {
  //     accessorial_codes = undefined;
  //   }
  // }

  return {
    pickup_date: isPastDate ? undefined : copiedData.pickup_date,
    due_date: isPastDate ? undefined : copiedData.due_date,
    truck_type: copiedData.truck_type,
    load_weight: copiedData.load_weight,
    mode: copiedData.mode,
    pickup_address: {
      ...copiedData.pickup_address,
      appointment_confirmed: false
    },
    delivery_address: {
      ...copiedData.delivery_address,
      appointment_confirmed: false
    },
    line_items: copiedData.line_items,
    accessorial_codes: copiedData.accessorial_codes,
    reference_number: copiedData.reference_number
  };
}

function addressToOption(address) {
  if (!address || !address.address_1) {
    return undefined;
  }
  if (address.address_detail) {
    return {
      label: address.address_detail.name,
      value: address
    };
  }
  if (address.name) {
    return {
      label: address.name,
      value: address
    };
  }
  return {
    label: `${address.address_1}, ${address.city}, ${address.state}`,
    value: address
  };
}

function addressErrors(type, errors) {
  const key = `${type}_address`;
  if (errors[key]) {
    return 'Full address is required.';
  }
  return undefined;
}

export default function RequestQuote({ location, history }) {
  const { copied_quote_id, copied_shipment_id } = location.query;
  const {
    state: { shipper }
  } = useAppState();

  const {
    state: { createdQuote, rate, tab },
    assignState
  } = useStateIn({
    tab: 'create',
    rate: null,
    createdQuote: null
  });

  const app = useApp();
  const shipmentsService = app.service('api/shipments');
  const quotesService = app.service('api/shipment-quotes');
  const addressDetailsService = app.service('api/address-details');
  const schema = quotesService.schema;

  const {
    result: copiedShipment,
    isLoading: isLoadingShipment,
    isError: isErrorShipment,
    error: errorShipment
  } = shipmentsService.useGet(copied_shipment_id, null, {
    enabled: !!copied_shipment_id
  });

  const {
    result: quote
    // isLoading: isLoadingQuote,
    // isError: isErrorQuote,
    // error: errorQuote
  } = quotesService.useGet(createdQuote?.id, null, {
    enabled: !!createdQuote,
    initialData: createdQuote
  });

  const {
    result: copiedQuote,
    isLoading: isLoadingCopiedQuote,
    isError: isErrorCopiedQuote,
    error: errorCopiedQuote
  } = quotesService.useGet(copied_quote_id, null, {
    enabled: !!copied_quote_id
  });

  const {
    result: defaultAddresses
    // isLoading: isLoadingDefaultAddresses,
    // isError: isErrorDefaultAddresses,
    // error: errorDefaultAddresses
  } = addressDetailsService.useFind(
    { query: { default_address: { $ne: null }, $limit: 2 } },
    {
      enabled: !copied_quote_id && !copied_shipment_id
    }
  );

  const copiedData = getInitialValues(
    copiedShipment || copiedQuote,
    defaultAddresses,
    !!shipper.address
  );

  const copiedQuoteId = copiedShipment?.shipment_quote_id || copiedQuote?.id;

  const isLoading = isLoadingShipment || isLoadingCopiedQuote; //|| isLoadingDefaultAddresses;
  const isError = isErrorShipment || isErrorCopiedQuote; //|| isErrorDefaultAddresses;
  const error = errorShipment || errorCopiedQuote; //|| errorDefaultAddresses;

  const handleSubmit = (data, formik) => {
    return quotesService
      .create(data)
      .then((quote) => {
        assignState({ createdQuote: quote });
        const scrollRef = document.getElementById('scroll-ref');
        if (scrollRef) {
          setTimeout(() => {
            scrollRef.scrollIntoView({ behavior: 'smooth' });
          }, 0);
        }
        formik.setSubmitting(false);
      })
      .catch((error) => {
        toast.errors(error);
        if (error.formik) {
          formik.setErrors(error.formik);
        }
        formik.setSubmitting(false);
      });
  };

  if (isLoading) {
    return (
      <div className="d-flex vh-100 align-items-center">
        <LoadingIndicator delay={250} />
      </div>
    );
  }

  if (isError) {
    return (
      <Container>
        <Row className="align-items-center justify-content-center vh-100">
          <Col lg="6" className="text-center">
            <img
              src={emptyImg}
              alt="Not Found"
              className="img-fluid animated fadeIn faster mb-5"
            />
            {error.code === 404 ? (
              <h1>Could not find that Quote</h1>
            ) : (
              <h1>{error.message}</h1>
            )}
            <Button
              to="/shipment-quotes"
              color="primary"
              size="lg"
              className="mr-3"
            >
              <Icon type="clipboard" className="mr-2" />
              Return to Quotes
            </Button>
            <Button to="/support" color="secondary" size="lg">
              <Icon type="send" className="mr-2" />
              Contact Straightline
            </Button>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <React.Fragment>
      <Formik
        onSubmit={handleSubmit}
        enableReinitialize={true}
        initialValues={copiedData}
        validationSchema={schema}
        render={({ isSubmitting, values }) => {
          return (
            <React.Fragment>
              <MapHeaderQuote quote={values} />
              <Container className="pb-3" fluid="md">
                <Card className="mt-n6" style={{ borderTopLeftRadius: 0 }}>
                  <div
                    className="bg-white rounded-top border border-bottom-0 px-4"
                    style={tabStyle}
                  >
                    <Nav className="header-tabs nav-tabs mb-0">
                      <NavItem>
                        <NavLink
                          onClick={() => assignState({ tab: 'create' })}
                          disabled={isSubmitting}
                          active={tab === 'create'}
                          className="py-2 px-2"
                        >
                          Quote
                        </NavLink>
                      </NavItem>
                      <NavItem>
                        <NavLink
                          onClick={() => assignState({ tab: 'import' })}
                          disabled={isSubmitting}
                          active={tab === 'import'}
                          className="py-2 px-2"
                        >
                          Import
                        </NavLink>
                      </NavItem>
                      <NavItem>
                        <NavLink
                          onClick={() => assignState({ tab: 'repeat' })}
                          disabled={isSubmitting}
                          active={tab === 'repeat'}
                          className="py-2 px-2"
                        >
                          Repeat
                        </NavLink>
                      </NavItem>
                    </Nav>
                  </div>
                  <TabContent activeTab={tab}>
                    <TabPane tabId="create">
                      <CreateQuoteTab
                        quote={quote}
                        rate={rate}
                        assignState={assignState}
                        copiedQuoteId={copiedQuoteId}
                      />
                    </TabPane>
                    <TabPane tabId="repeat" unmountOnExit>
                      <RepeatQuoteTab assignState={assignState} />
                    </TabPane>
                    <TabPane tabId="import" unmountOnExit>
                      <ImportQuoteTab assignState={assignState} />
                    </TabPane>
                  </TabContent>
                </Card>
              </Container>
            </React.Fragment>
          );
        }}
      />
      {quote && rate && (
        <BookShipmentModal
          open={!!rate}
          quote={quote}
          rate={rate}
          copiedQuoteId={copiedQuoteId}
          onCancel={() => {
            assignState({ rate: null });
          }}
          onSuccess={(shipment) => {
            history.push(`/shipments/${shipment.id}`);
          }}
        />
      )}
    </React.Fragment>
  );
}

function isPastPickup({ pickup_address, pickup_date }, hour) {
  if (!pickup_address?.timezone || !pickup_date) {
    return false;
  }
  return isPastDateTime(pickup_address.timezone, pickup_date, hour);
}

function isPastDateTime(timezone, dateString, hour) {
  // Get the current date and time in the specified timezone
  const now = new Date().toLocaleString('en-US', { timeZone: timezone });

  // Extract the current year, month, and day from the date
  const [currentMonth, currentDay, currentYear] = new Date(now)
    .toLocaleDateString('en-US', { timeZone: timezone })
    .split('/')
    .map(Number);

  // Extract the input year, month, and day from the date string
  const [inputYear, inputMonth, inputDay] = dateString.split('-').map(Number);

  // Check if the date is the same day as today
  if (
    currentYear === inputYear &&
    currentMonth === inputMonth &&
    currentDay === inputDay
  ) {
    // Get the current hour from the date
    const currentHour = new Date(now).getHours();

    // Compare the current hour with the specified hour
    if (currentHour >= hour) {
      return true;
    } else {
      return false;
    }
  }

  return false;
}

function CreateQuoteTab({ assignState, quote, rate, copiedQuoteId }) {
  const { isSubmitting, values, errors, setValues } = formik.useFormikContext();
  const [manualDates, setManualDates] = useState(false);
  const {
    state: { shipper }
  } = useAppState();

  const isIllegalParcel =
    isParcel(values) &&
    values.pickup_address &&
    values.delivery_address &&
    (values.pickup_address.country !== 'US' ||
      values.delivery_address.country !== 'US');

  const pastPickup =
    (isLTL(values) && isPastPickup(values, 15)) ||
    (isFTL(values) && isPastPickup(values, 17));

  const handleReset = () => {
    assignState({ createdQuote: null });
  };

  const handleModeChange = (mode) => {
    if (mode !== FTL) {
      setManualDates(false);
    }

    if (mode === LTL) {
      return setValues({
        ...values,
        mode: LTL,
        truck_type: TRUCK_TYPE.DRY_VAN,
        accessorial_codes: values.accessorial_codes
          ?.filter((code) => {
            return code !== 'TANK';
          })
          .map((code) => {
            if (code === 'DVRPU') {
              return 'INPU';
            }
            if (code === 'DVRDEL') {
              return 'INDEL';
            }
            return code;
          })
          .filter((code, index, self) => {
            return self.indexOf(code) === index;
          }),
        line_items: (
          values.line_items || initialLineItems(!!shipper.address)
        ).map((item) => {
          if (item.type === 'BOX') {
            return {
              ...item,
              type: 'PLT'
            };
          }
          return item;
        })
      });
    }

    if (mode === PARCEL) {
      return setValues({
        ...values,
        mode: PARCEL,
        truck_type: TRUCK_TYPE.DRY_VAN,
        accessorial_codes: values.accessorial_codes?.filter((code) => {
          return ['RESPU', 'RESDEL'].includes(code);
        }),
        line_items: (
          values.line_items || initialLineItems(!!shipper.address)
        ).map((item) => {
          if (item.type === 'PLT') {
            return {
              ...item,
              type: 'BOX'
            };
          }
          return item;
        })
      }).filter((code, index, self) => {
        return self.indexOf(code) === index;
      });
    }

    return setValues({
      ...values,
      mode: FTL,
      accessorial_codes: values.accessorial_codes
        ?.map((code) => {
          if (code === 'INPU') {
            return 'DVRPU';
          }
          if (code === 'INDEL') {
            return 'DVRDEL';
          }
          return code;
        })
        .filter((code, index, self) => {
          return self.indexOf(code) === index;
        })
    });
  };

  const isInsured = values.line_items?.some((item) => item.insurance);

  return (
    <CardBody className="px-3 px-md-4">
      <QuoteLoader show={isSubmitting} />
      <Form>
        <Row>
          <Col xl="6" className="mb-4 mb-xl-0">
            <div className="mb-4">
              <Label>Shipment Mode</Label>
              <FormInput
                type="radio-buttons"
                name="mode"
                emptyValue={null}
                options={ENABLED_MODES}
                disabled={isSubmitting || !!quote}
                onChange={(event) => {
                  return handleModeChange(event.target.value);
                }}
              />
              <div className="mt-3">
                {
                  ENABLED_MODES.find((mode) => mode.value === values.mode)
                    ?.description
                }
              </div>
            </div>
            <div className="mb-4">
              <Label>Shipment Insurance</Label>
              <Input
                type="radio-buttons"
                name="insurance"
                value={isInsured ? 'yes' : 'no'}
                options={[
                  {
                    label: 'Insured',
                    value: 'yes'
                  },
                  {
                    label: 'Not Insured',
                    value: 'no'
                  }
                ]}
                disabled={isSubmitting || !!quote || !shipper.address}
                onChange={(event) => {
                  const shouldInsure = event.target.value === 'yes';

                  if (shouldInsure) {
                    if (isInsured) {
                      return;
                    }

                    return setValues({
                      ...values,
                      line_items: values.line_items.map((item) => {
                        return {
                          ...item,
                          insurance: {
                            amount: undefined,
                            commodity: undefined
                          }
                        };
                      })
                    });
                  }

                  if (isInsured) {
                    return setValues({
                      ...values,
                      line_items: values.line_items.map((item) => {
                        return {
                          ...item,
                          insurance: null
                        };
                      })
                    });
                  }
                }}
              />
              {!shipper.address && (
                <div className="mt-3">
                  You must update your billing address before adding insurance.
                  Please visit the <Link to="/settings">settings page</Link>.
                </div>
              )}
              <div className="mt-3">
                {isParcel(values) ? (
                  <span>
                    Add insurance from{' '}
                    <a
                      target="_blank"
                      href="https://www.shipengine.com/shipsurance-tos"
                      rel="noreferrer"
                    >
                      Shipengine
                    </a>
                    . Straightline partners with Shipengine to provide instant,
                    no-obligation insurance quotes. Insurance is calculated for
                    each rate and provider. Labels will include insurance and be
                    included with the package.
                  </span>
                ) : (
                  <span>
                    Add insurance from{' '}
                    <a
                      target="_blank"
                      href="https://www.loadsure.net"
                      rel="noreferrer"
                    >
                      Loadsure
                    </a>
                    . Straightline partners with Loadsure to provide instant,
                    no-obligation insurance quotes. Loadsure's dynamic rating
                    engine analyzes high-resolution data in real time, creating
                    an accurate risk score for every shipment.
                  </span>
                )}
              </div>
            </div>
            <div className="">
              <Label>Truck Type</Label>
              <FormInput
                type="radio-buttons"
                name="truck_type"
                emptyValue={null}
                options={ENABLED_TRUCK_TYPES}
                disabled={isSubmitting || !!quote || !isFTL(values)}
              />
              <div className="mt-3">
                {
                  ENABLED_TRUCK_TYPES.find(
                    (type) => type.value === values.truck_type
                  )?.description
                }
              </div>

              <FormFeedback name="truck_type" detached />
            </div>
          </Col>
          <Col xl="6">
            <Label>Pickup Date</Label>
            <div
              style={
                isSubmitting || !!quote
                  ? { opacity: 0.6, pointerEvents: 'none' }
                  : {}
              }
            >
              {manualDates ? (
                <QuoteCalendar setValues={setValues} values={values} />
              ) : (
                <PickupCalendar setValues={setValues} values={values} />
              )}

              {manualDates && (
                <div className="text-secondary mt-3 mb-0">
                  Click a date twice to select same day delivery.
                  {isFTL(values) && (
                    <>
                      {' '}
                      To use estimated delivery dates,{' '}
                      <span
                        className="text-primary pointer"
                        onClick={() => setManualDates(false)}
                      >
                        click here
                      </span>
                      .
                    </>
                  )}
                </div>
              )}

              {!manualDates && (
                <div className="text-secondary mt-3">
                  Delivery date is estimated based on distance and lane
                  capacity.
                  {isFTL(values) && (
                    <>
                      {' '}
                      To manually select a delivery date,{' '}
                      <span
                        className="text-primary pointer"
                        onClick={() => setManualDates(true)}
                      >
                        click here
                      </span>
                      .
                    </>
                  )}
                </div>
              )}

              <PastPickup values={values} pastPickup={pastPickup} />
            </div>
            <div className="is-invalid">
              <FormFeedback name="pickup_date" />
              <FormFeedback name="due_date" />
            </div>
          </Col>
        </Row>

        <hr className="my-5" />

        <Row className="mb-4">
          <Col xl="7">
            <div className="mb-3">
              <div className="d-flex justify-content-between">
                <Label>Pickup Details</Label>
                <SaveAddress
                  disabled={isSubmitting || !!quote}
                  address={values.pickup_address}
                  onComplete={(addressDetail) => {
                    setValues({
                      ...values,
                      pickup_address: {
                        ...values.pickup_address,
                        ...addressDetail.address
                      }
                    });
                  }}
                />
              </div>
              <FormInput
                type="address"
                name="pickup_address"
                disabled={isSubmitting || !!quote}
                placeholder="Type business name or address"
                addressToOption={addressToOption}
                isClearable
                useAddressDetails
                useAddress2
              />
              <FormFeedback>{addressErrors('pickup', errors)}</FormFeedback>
            </div>
            <div className="mb-3">
              <FormInput
                type="check-buttons"
                name="accessorial_codes"
                emptyValue={null}
                options={
                  isParcel(values)
                    ? PARCEL_CODES.PICKUP
                    : isFTL(values)
                    ? FTL_CODES.PICKUP
                    : LTL_CODES.PICKUP
                }
                disabled={isSubmitting || !!quote}
              />
              <FormFeedback name="accessorial_codes" />
            </div>
          </Col>
          <Col xl="5">
            <div className="mt-3 mt-lg-4">
              <AddressCardPreview
                address={values.pickup_address}
                date={values.pickup_date}
                className="border rounded p-3"
                size="140x140"
                showCurrentTime
                showWeather
              />
            </div>
          </Col>
        </Row>

        <Row>
          <Col xl="7">
            <div className="mb-3">
              <div className="d-flex justify-content-between">
                <Label>Delivery Details</Label>
                <SaveAddress
                  disabled={isSubmitting || !!quote}
                  address={values.delivery_address}
                  onComplete={(addressDetail) => {
                    setValues({
                      ...values,
                      delivery_address: {
                        ...values.delivery_address,
                        ...addressDetail.address
                      }
                    });
                  }}
                />
              </div>
              <FormInput
                type="address"
                name="delivery_address"
                disabled={isSubmitting || !!quote}
                placeholder="Type business name or address"
                addressToOption={addressToOption}
                isClearable
                useAddressDetails
                useAddress2
              />
              <FormFeedback>{addressErrors('delivery', errors)}</FormFeedback>
            </div>
            <div className="mb-3">
              <FormInput
                type="check-buttons"
                name="accessorial_codes"
                emptyValue={null}
                options={
                  isParcel(values)
                    ? PARCEL_CODES.DELIVERY
                    : isFTL(values)
                    ? FTL_CODES.DELIVERY
                    : LTL_CODES.DELIVERY
                }
                disabled={isSubmitting || !!quote}
              />
              <FormFeedback name="accessorial_codes" />
            </div>
          </Col>
          <Col xl="5">
            <div className="mt-3 mt-lg-4">
              <AddressCardPreview
                address={values.delivery_address}
                date={values.due_date}
                className="border rounded p-3"
                size="140x140"
                showCurrentTime
                showWeather
              />
            </div>
          </Col>
        </Row>
        <div>
          {isIllegalParcel && (
            <div className="mb-4 text-danger">
              Parcel shipments are currently only supported in the United
              States.
            </div>
          )}
        </div>

        <hr className="my-5" />

        <div>
          <LineItems
            setValues={setValues}
            values={values}
            disabled={isSubmitting || !!quote}
          />
        </div>

        <div>
          <Label>Additional Accessorials</Label>
          <div className="mb-3">
            <FormInput
              id="hazmat-checkbox"
              name="hazmat"
              label="Hazmat: this shipment contains hazardous or dangerous materials"
              type="checkbox"
              checked={values.accessorial_codes?.includes('HAZM')}
              onChange={() => {
                if (!values.accessorial_codes) {
                  return setValues({
                    ...values,
                    accessorial_codes: ['HAZM']
                  });
                }

                if (values.accessorial_codes.includes('HAZM')) {
                  const value = values.accessorial_codes.filter(
                    (code) => code !== 'HAZM'
                  );
                  return setValues({
                    ...values,
                    accessorial_codes: value.length ? value : null
                  });
                }

                return setValues({
                  ...values,
                  accessorial_codes: [...values.accessorial_codes, 'HAZM']
                });
              }}
              disabled={isSubmitting || !!quote}
            />
          </div>
          {isFTL(values.mode) && (
            <div className="mb-3">
              <FormInput
                id="tanker-endorsed-checkbox"
                name="tanker-endorsed"
                label="Tanker Endorsed: driver is certified to transport liquids"
                type="checkbox"
                checked={values.accessorial_codes?.includes('TANK')}
                onChange={() => {
                  if (!values.accessorial_codes) {
                    return setValues({
                      ...values,
                      accessorial_codes: ['TANK']
                    });
                  }

                  if (values.accessorial_codes.includes('TANK')) {
                    const value = values.accessorial_codes.filter(
                      (code) => code !== 'TANK'
                    );
                    return setValues({
                      ...values,
                      accessorial_codes: value.length ? value : null
                    });
                  }

                  return setValues({
                    ...values,
                    accessorial_codes: [...values.accessorial_codes, 'TANK']
                  });
                }}
                disabled={isSubmitting || !!quote}
              />
            </div>
          )}
        </div>

        <div className="mt-4">
          <Label>Reference Number or Pickup Number</Label>
          <FormInput
            name="reference_number"
            placeholder="PO number, RFQ number, or any other internal reference"
            disabled={isSubmitting || !!quote}
          />
          <div className="text-muted mt-1 mb-2">
            Carriers may use this number as the pickup number for your shipment.
            You can add additional reference numbers and any other relevant
            information in the 'Notes' section when booking a quote.
          </div>
          <FormFeedback name="reference_number" />
        </div>

        <hr className="my-5" id="scroll-ref" />

        {!quote && (
          <Row className="mb-n3 align-items-center">
            <Col xl="auto" className="mb-3">
              <Button
                color="primary"
                className={`btn-xs-block btn-sm-block btn-md-block btn-lg-block px-6 ${
                  isSubmitting ? 'is-loading' : ''
                }`}
                disabled={isSubmitting || isIllegalParcel || pastPickup}
                type="submit"
              >
                <Icon type="clipboard" className="mr-2" />
                Request Quote
              </Button>
            </Col>
            <Col xl="auto" className="mb-3">
              <div className="text-secondary" style={{ lineHeight: 1.2 }}>
                Complete the form to receive an instant and no-obligation quote.
                <br className="d-none d-lg-block" />
                <span className="d-lg-none mr-1"></span>
                Once quoted, you can make an offer or book a shipment.
              </div>
            </Col>
          </Row>
        )}

        {quote && (
          <React.Fragment>
            <div className="mb-4">
              <RatePicker
                action={true}
                onChange={({ target }) => {
                  assignState({ rate: target.rate });
                }}
                value={rate?.id}
                quote={quote}
              />
            </div>
            <div className="font-weight-bold mb-2">
              This quote will expire. Click a rate above to see more details and
              lock in this price!
            </div>
            <div className="text-muted mb-4">
              This price includes line-haul & fuel surcharge. All delivery dates
              and times are estimates. For a list of all possible accessorial
              charges, refer to the{' '}
              <a href="/support" target="_blank" rel="noopener noreferrer">
                support
              </a>{' '}
              page.
            </div>
            <div className="mb-n3">
              <Button
                type="button"
                className="btn-white btn-xs-block btn-sm-block btn-md-block px-6 mb-3 mr-3 "
                onClick={handleReset}
              >
                Try Again
              </Button>
              <Button
                to={`/quotes/${quote.id}`}
                query={{ copied_quote_id: copiedQuoteId }}
                color="primary"
                className="btn-xs-block btn-sm-block btn-md-block mb-3 px-6"
              >
                <Icon type="check-square" className="mr-2" />
                View Quote
              </Button>
            </div>
          </React.Fragment>
        )}
      </Form>
    </CardBody>
  );
}

function RepeatQuoteTab({ assignState }) {
  const { setValues } = formik.useFormikContext();
  const app = useApp();
  const [query, setQuery] = useState({});
  const [text, setText] = useState('');
  const searchRef = useRef(null);

  useEffect(() => {
    if (searchRef.current) {
      searchRef.current.focus();
    }
  }, []);

  // eslint-disable-next-line
    const debouncedHandleChange = useCallback(
    debounce((value) => {
      setQuery((query) => {
        return { ...query, $search: value };
      });
    }, 300),
    []
  );

  const handleSearchChange = ({ target: { value } }) => {
    setText(value);
    debouncedHandleChange(value);
  };

  const handleModeChange = (mode) => {
    setQuery((query) => {
      if (query.mode === mode) {
        return { ...query, mode: undefined };
      }
      return { ...query, mode };
    });
  };

  const handleClick = async (quote) => {
    const isPastDate =
      new Date(quote.pickup_date) <
      new Date(formatDate(new Date(), 'dateOnly'));

    let nextDay = addDays(new Date(), 1);
    if (isWeekend(nextDay)) {
      const dayOfWeekEnd = nextDay.getDay();
      if (dayOfWeekEnd === 6) {
        nextDay = addDays(nextDay, 2);
      } else {
        nextDay = addDays(nextDay, 1);
      }
    }

    nextDay = formatDate(nextDay, 'dateOnly');

    const data = {
      pickup_date: isPastDate ? nextDay : quote.pickup_date,
      due_date: isPastDate ? undefined : quote.due_date,
      truck_type: quote.truck_type,
      load_weight: quote.load_weight,
      mode: quote.mode,
      pickup_address: {
        ...quote.pickup_address,
        appointment_confirmed: false
      },
      delivery_address: {
        ...quote.delivery_address,
        appointment_confirmed: false
      },
      line_items: quote.line_items,
      accessorial_codes: quote.accessorial_codes
    };

    if (quote.accepted_at) {
      const shipment = await app.service('api/shipments').findOne({
        query: {
          shipment_quote_id: quote._id,
          $select: ['pickup_address', 'delivery_address']
        }
      });

      if (shipment) {
        data.pickup_address = {
          ...data.pickup_address,
          contact: shipment.pickup_address.contact,
          phone: shipment.pickup_address.phone,
          email: shipment.pickup_address.email,
          appointment: shipment.pickup_address.appointment
        };
        data.delivery_address = {
          ...data.delivery_address,
          contact: shipment.delivery_address.contact,
          phone: shipment.delivery_address.phone,
          email: shipment.delivery_address.email,
          appointment: shipment.delivery_address.appointment
        };
      }
    }

    setValues(data);
    assignState({ tab: 'create', quote: null });

    window.scrollTo({ top: 0 });
  };

  const {
    result: quotes,
    isLoading,
    error
  } = app.service('api/shipment-quotes').useFind(
    {
      query: {
        $limit: 20,
        $sort: { created_at: -1 },
        ...query
      }
    },
    {
      keepPreviousData: true
    }
  );

  if (error) {
    return (
      <CardBody className="p-0" style={{ minHeight: 'calc(100vh - 216px)' }}>
        <div className="text-center py-5 text-danger">
          {error.message || 'An error occurred'}
        </div>
      </CardBody>
    );
  }

  return (
    <CardBody className="p-0" style={{ minHeight: 'calc(100vh - 216px)' }}>
      <div className="px-4 py-3">
        <Row className="align-items-center">
          <Col className="col-auto pr-0">
            <Icon type="search" className="text-muted" />
          </Col>
          <Col>
            <Input
              innerRef={searchRef}
              value={text}
              onChange={handleSearchChange}
              className="form-control-flush"
              placeholder="Search"
            />
          </Col>
          <Col className="col-auto p-0">
            <ButtonGroup>
              <Button
                size="sm"
                color="white"
                type="button"
                active={query.mode === MODE.FTL}
                onClick={() => handleModeChange(MODE.FTL)}
              >
                FTL
              </Button>
              <Button
                size="sm"
                color="white"
                type="button"
                active={query.mode === MODE.LTL}
                onClick={() => handleModeChange(MODE.LTL)}
              >
                LTL
              </Button>
              <Button
                size="sm"
                color="white"
                type="button"
                active={query.mode === MODE.PARCEL}
                onClick={() => handleModeChange(MODE.PARCEL)}
              >
                Parcel
              </Button>
            </ButtonGroup>
          </Col>
        </Row>
      </div>
      <div className="table-responsive card-table border-bottom">
        <Table className="table table-nowrap card-table table-hover">
          <TableHead>
            <TableRow>
              <TableHeader className="py-3">Price</TableHeader>
              <TableHeader className="py-3">Pickup</TableHeader>
              <TableHeader className="py-3">Delivery</TableHeader>
              <TableHeader className="py-3">Details</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody className="list">
            {quotes?.data.map((quote) => {
              return (
                <TableRow
                  key={quote.id}
                  onClick={() => handleClick(quote)}
                  className="pointer"
                >
                  <TableData>
                    <PriceTableData quote={quote} />
                  </TableData>
                  <TableData>
                    <div>
                      {`${quote.pickup_address.city}, ${quote.pickup_address.state}`}
                      {quote.pickup_address.name && (
                        <span id={`pickup-name-${quote.id}`} className="ml-1">
                          <Icon type="info" className="text-secondary" />
                          <UncontrolledTooltip
                            target={`pickup-name-${quote.id}`}
                          >
                            {quote.pickup_address.name}
                          </UncontrolledTooltip>
                        </span>
                      )}
                    </div>
                    <div>{formatDate(quote.pickup_date, 'LLL do, yyyy')}</div>
                  </TableData>
                  <TableData
                    className="px-3 px-xl-4"
                    style={{ position: 'relative' }}
                  >
                    <div style={{ position: 'absolute', left: -12, top: 21 }}>
                      <Icon
                        type="arrow-right text-success"
                        style={{ fontSize: 25 }}
                      />
                    </div>
                    <div>
                      {`${quote.delivery_address.city}, ${quote.delivery_address.state}`}
                      {quote.delivery_address.name && (
                        <span id={`delivery-name-${quote.id}`} className="ml-1">
                          <Icon type="info" className="text-secondary" />
                          <UncontrolledTooltip
                            target={`delivery-name-${quote.id}`}
                          >
                            {quote.delivery_address.name}
                          </UncontrolledTooltip>
                        </span>
                      )}
                    </div>
                    <div>{formatDate(quote.due_date, 'LLL do, yyyy')}</div>
                  </TableData>
                  <TableData>
                    <QuoteUnits quote={quote} />
                  </TableData>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
      {!isLoading && quotes?.total === 0 && (
        <div className="text-center mt-6 text-muted">
          {text ? 'No quotes found' : 'No quotes available'}
        </div>
      )}
    </CardBody>
  );
}

function PriceTableData({ quote }) {
  if (isAccepted(quote)) {
    const rate = quote.rates.find((rate) => {
      return rate.id === quote.rate_id;
    });

    return (
      <div>
        <div className="font-weight-bold text-success">
          <Amount min={quote.rates_range.min} max={quote.rates_range.max} />
        </div>
        <div className="text-success">
          Accepted for{' '}
          {rate && formatMoney(rate.price.shipper_price, { precision: 0 })}
        </div>
      </div>
    );
  }

  if (isExpired(quote)) {
    return (
      <div>
        <div className="font-weight-bold text-danger">
          <Amount del min={quote.rates_range.min} max={quote.rates_range.max} />
        </div>
        <div className="text-danger">
          Expired on {formatDate(quote.expired_at)}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="font-weight-bold text-primary">
        <Amount min={quote.rates_range.min} max={quote.rates_range.max} />
      </div>
      <div className="text-primary">
        expires {formatDistanceToNowStrict(quote.expired_at)}
      </div>
    </div>
  );
}

const addressName = (address) => {
  let string = '';

  ['address_1', 'address_2', 'city', 'state'].forEach((key) => {
    if (address[key]) {
      string += `${address[key]}`;
    }
    if (address[key] && key !== 'state') {
      string += ', ';
    }
  });

  if (address.name) {
    string = `${address.name}\n${string}`;
  }

  return string;
};

const punctuationMap = {
  period: '.',
  comma: ',',
  'question mark': '?',
  'exclamation mark': '!'
};

function formatSpeechText(text) {
  const punctuatedText = text.replace(
    new RegExp(`\\b(${Object.keys(punctuationMap).join('|')})\\b`, 'gi'),
    (match) => punctuationMap[match.toLowerCase()]
  );

  const sentences = punctuatedText.split(/([.!?])/g).reduce((acc, cur) => {
    if (/[.!?]/.test(cur)) {
      // Add punctuation to the previous sentence
      acc[acc.length - 1] += cur;
    } else if (cur.trim()) {
      // Add trimmed text as a new sentence
      acc.push(cur.trim());
    }
    return acc;
  }, []);

  const formattedText = sentences
    .map((sentence) => sentence.charAt(0).toUpperCase() + sentence.slice(1))
    .join(' ');

  return formattedText.trim();
}

function ImportQuoteTab({ assignState }) {
  const app = useApp();
  const [text, setText] = useState('');
  const [isSubmitting, setSubmitting] = useState(false);
  const [recognition, setRecognition] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const { setValues } = formik.useFormikContext();
  const { xl } = useScreenSize();

  useEffect(() => {
    return () => {
      if (recognition && recognition.stop) {
        recognition.stop();
      }
    };
  }, [recognition]);

  const toggleModal = () => setModalOpen(!modalOpen);

  const handleSubmit = async () => {
    if (recognition) {
      handleStopSpeech();
    }
    setSubmitting(true);
    app
      .service('api/shipment-parses')
      .create({ text })
      .then((data) => {
        setValues(data);
        assignState({ tab: 'create' });
      })
      .catch((error) => {
        toast.errors(error);
        setSubmitting(false);
      });
  };

  const handleStartSpeech = async () => {
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SpeechRecognition) {
      toast.error('Speech Recognition not available');
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';

    let finalTranscript = '';

    recognition.onresult = (event) => {
      let transcript = '';

      if (typeof event.results == 'undefined') {
        handleStopSpeech();
        return;
      }

      for (let i = event.resultIndex; i < event.results.length; ++i) {
        const result = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTranscript += result;
        } else {
          transcript += result;
        }
      }

      const formattedText = formatSpeechText(finalTranscript + transcript);

      setText(formattedText);
    };

    recognition.onerror = (event) => {
      toast.errors(event.error);
      setRecognition(null);
    };

    recognition.start();

    setRecognition(recognition);
    setText('');
  };

  const handleStopSpeech = () => {
    if (recognition?.stop) {
      recognition.stop();
    }
    setRecognition(null);
  };

  const { result: shipment } = app
    .service('api/shipments')
    .useFindOne(
      { query: { $sort: { created_at: -1 } } },
      { keepPreviousData: true }
    );

  let placeholder = 'Copy/paste or type text to import data...';

  if (recognition) {
    placeholder = 'Start speaking to import data...';
  } else if (shipment) {
    const item = shipment.line_items[0];
    const pallets = `${item.units} ${
      item.units > 1 ? 'Pallets' : 'Pallet'
    } of ${item.description}\n${formatNumber(item.total_weight)}lbs, ${
      item.length
    }x${item.width}x${item.height}`;
    placeholder = `Pickup\n${addressName(
      shipment.pickup_address
    )}\n\nDelivery\n${addressName(shipment.delivery_address)}\n\n${pallets}`;
  }

  const containerHeight = 'calc(100vh - 216px)';
  const inputHeight = xl ? 'calc(100vh - 355px)' : 'calc(100vh - 402px)';

  return (
    <div style={{ height: containerHeight }}>
      <MoreInfoModal open={modalOpen} toggle={toggleModal} />
      <div className="d-flex flex-column justify-content-between h-100">
        <div className="p-4 position-relative" style={{ overflowY: 'auto' }}>
          <div
            style={{
              position: 'absolute',
              bottom: '2.25rem',
              right: '2.25rem',
              zIndex: 1
            }}
          >
            <Button
              color={recognition ? 'outline-danger' : 'white'}
              className="px-4"
              size="sm"
              disabled={isSubmitting}
              onClick={recognition ? handleStopSpeech : handleStartSpeech}
              style={{ minWidth: 114 }}
            >
              <Icon
                type={recognition ? 'stop-circle' : 'mic'}
                className={`mr-2 ${
                  recognition ? 'animated flash infinite slower' : ''
                }`}
              />
              {recognition ? ' Stop' : 'Speak'}
            </Button>
          </div>
          <textarea
            className="form-control"
            style={{
              height: inputHeight,
              fontSize: '1.05rem'
            }}
            placeholder={placeholder}
            value={text}
            onChange={(event) => {
              if (recognition) {
                return;
              }
              setText(event.target.value);
            }}
            disabled={isSubmitting}
          />
        </div>
        <div className="p-4 border-top">
          <Row className="mb-n3 align-items-center">
            <Col xl="auto" className="mb-3">
              <Button
                color="primary"
                className={`btn-xs-block btn-sm-block btn-md-block btn-lg-block px-6 ${
                  isSubmitting ? 'is-loading' : ''
                }`}
                disabled={isSubmitting || !text}
                onClick={handleSubmit}
              >
                <Icon type="upload" className="mr-2" />
                Import Data
              </Button>
            </Col>
            <Col xl="" className="mb-3">
              <div className="text-secondary" style={{ lineHeight: 1.15 }}>
                Straightline utilizes AI and your shipment history to transform
                text into quotable data. Type or paste some text to get started.
                Or, click "Speak" to dictate your quote details.{' '}
                <span className="text-primary pointer" onClick={toggleModal}>
                  More info and tips
                </span>
              </div>
            </Col>
          </Row>
        </div>
      </div>
    </div>
  );
}

function MoreInfoModal({ open, toggle }) {
  return (
    <Modal
      isOpen={open}
      toggle={toggle}
      scrollable
      centered
      modalClassName="modal-fullscreen-md"
    >
      {() => {
        return <MoreInfoModalContent toggle={toggle} />;
      }}
    </Modal>
  );
}

function MoreInfoModalContent({ toggle }) {
  const app = useApp();

  const { result: addresses, isLoading: addressesLoading } = app
    .service('api/address-details')
    .useFind(
      {
        query: {
          $limit: 2,
          $sort: { created_at: -1 }
        }
      },
      {
        keepPreviousData: true
      }
    );

  const firstAddress = addresses?.data[0];
  const secondAddress = addresses?.data[1];

  const { result: shipment, isLoading: shipmentLoading } = app
    .service('api/shipments')
    .useFindOne(
      { query: { $sort: { created_at: -1 } } },
      { keepPreviousData: true }
    );

  return (
    <React.Fragment>
      <ModalBody>
        <h2>Importing quotes in Straightline</h2>
        <p>
          The Straightline "Import Quote" feature makes it easy to get quotes
          from wherever your data may come from. You can copy/paste text from
          your emails, TMS, Excel, or anywhere else. You can also use the
          "Speak" feature to transform your spoken words into text. Straightline
          then uses AI trained on your latest shipment history to transform that
          text into quotable data.
        </p>
        <p>
          Results from the AI may be unreliable or incomplete. Be sure to
          double-check the data before continuing.
        </p>
        <h2>Tips and Tricks</h2>
        <ul className="pl-4 mb-4">
          <li className="mb-2">
            Use your saved addresses or addresses you've shipped to before.
            {!addressesLoading && (
              <span>
                Try saying "Pickup at{' '}
                {firstAddress ? firstAddress.address.name : 'my last pickup'}"
                or "Drop off at{' '}
                {secondAddress
                  ? secondAddress.address.name
                  : 'my last drop off'}
                ".
              </span>
            )}
          </li>
          <li className="mb-2">
            Remove extra data. When copy/pasting data, be sure to remove any
            extra cruft not relative to the shipment.
          </li>
          <li>
            Reference other shipments in your text.{' '}
            {!shipmentLoading && (
              <span>
                Try saying "
                {shipment
                  ? `Repeat shipment #${shipment.id} but deliver to...`
                  : 'Repeat my last shipment but deliver to...'}
                "
              </span>
            )}{' '}
            The AI is only trained on your last 50 shipments so results may
            vary.
          </li>
        </ul>
        <h2>Help us make it better</h2>
        <p>
          While we hope this tool is helpful, we also know that AI is imperfect.
          Help us make this feature better by giving us some feedback. Tell us
          how it's working, how it's breaking, and what kinda of data you are
          using.
        </p>
        <p>
          <Icon type="phone" className="mr-3" />
          <a href={`tel:${process.env.REACT_APP_SUPPORT_PHONE}`}>
            {process.env.REACT_APP_SUPPORT_PHONE}
          </a>
        </p>
        <p className="mb-0">
          <Icon type="mail" className="mr-3" />
          <a href={`mailto:${process.env.REACT_APP_SUPPORT_EMAIL}`}>
            {process.env.REACT_APP_SUPPORT_EMAIL}
          </a>
        </p>
      </ModalBody>
      <ModalFooter className="py-3">
        <Button
          color="primary"
          type="button"
          className="btn-xs-block px-6"
          onClick={toggle}
        >
          Close
        </Button>
      </ModalFooter>
    </React.Fragment>
  );
}

function PastPickup({ values, pastPickup }) {
  if (!pastPickup) {
    return null;
  }
  const timezone = parseTimezone(values.pickup_address.timezone);
  const time = `${isLTL(values) ? `3PM` : `5PM`} ${timezone.value}`;
  return (
    <div className="mb-4 text-danger">
      Shipments cannot be scheduled for same day pickup after {time} in{' '}
      {values.pickup_address.city}, {values.pickup_address.state}. Please select
      a later date.
    </div>
  );
}

const randomProgress = (min, max) => {
  return Math.random() * (max - min) + min;
};

const QuoteLoader = ({ show = true }) => {
  const [state, setState] = useState({
    text: 'Contacting carriers...',
    progress: randomProgress(5, 15)
  });
  useEffect(() => {
    if (show) {
      const timeouts = [];
      timeouts.push(
        setTimeout(() => {
          setState((state) => ({
            ...state,
            text: 'Negotiating rates...',
            progress: randomProgress(15, 33)
          }));
        }, randomProgress(2000, 3000)),
        setTimeout(() => {
          setState((state) => ({
            ...state,
            text: 'Finalizing price...',
            progress: randomProgress(33, 66)
          }));
        }, randomProgress(3000, 5000)),
        setTimeout(() => {
          setState((state) => ({
            ...state,
            text: 'Generating quote...',
            progress: randomProgress(66, 99)
          }));
        }, randomProgress(5000, 7000)),
        setTimeout(() => {
          setState((state) => ({
            ...state,
            text: 'Finalizing quote...',
            progress: 100
          }));
        }, randomProgress(7000, 9000))
      );
      return () => {
        timeouts.forEach(clearTimeout);
      };
    }

    setState({
      text: 'Contacting carriers...',
      progress: randomProgress(5, 15)
    });
  }, [show]);

  if (!show) {
    return null;
  }

  return (
    <div
      className="shadow-lg rounded animated fadeIn"
      style={{
        position: 'absolute',
        zIndex: 100,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        // background: 'rgba(249, 251, 253, 0.90)'
        background: 'rgba(255, 255, 255, 0.90)'
      }}
    >
      <div className="w-100">
        <LoadingIndicator delay={100} text={state.text} />
        <div className="d-flex justify-content-center w-100 animated fadeIn">
          <div className="w-100" style={{ maxWidth: 600, zIndex: 101 }}>
            <Progress
              animated
              value={state.progress}
              style={{ height: 5, marginTop: -3 }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const estimateLTLDays = (miles) => {
  const days = Math.ceil(miles / 350);
  return days;
};

const calculateDueDate = (date, days) => {
  let due_date = addDays(date, days);

  if (isWeekend(due_date)) {
    const dayOfWeekEnd = due_date.getDay();
    if (dayOfWeekEnd === 6) {
      due_date = addDays(due_date, 2);
    } else {
      due_date = addDays(due_date, 1);
    }
  }

  return due_date;
};

const PickupCalendar = ({ setValues, values }) => {
  const app = useApp();
  const [isLoading, setIsLoading] = useState(false);

  const {
    pickup_date,
    due_date,
    mode,
    truck_type,
    pickup_address,
    delivery_address
  } = values;

  const value = pickup_date ? parseDate(pickup_date) : null;

  useEffect(() => {
    const estimateDate = async () => {
      if (
        !pickup_address?.longitude ||
        !pickup_address?.latitude ||
        !delivery_address?.longitude ||
        !delivery_address?.latitude ||
        !mode ||
        !truck_type ||
        !pickup_date
      ) {
        if (due_date) {
          setValues((values) => ({
            ...values,
            due_date: undefined
          }));
        }

        return;
      }

      if (due_date) {
        setValues((values) => ({
          ...values,
          due_date: undefined
        }));
      }

      try {
        setIsLoading(true);

        const route = await app.service('api/shipment-routes').find({
          query: {
            pickup_address: pickup_address,
            delivery_address: delivery_address
          }
        });

        if (route) {
          if (mode === MODE.FTL) {
            const today = startOfDay(new Date());
            const pickupDay = startOfDay(pickup_date);
            let date = addHours(new Date(), 2);

            if (!isSameDay(today, pickupDay)) {
              date = toZonedTime(pickup_date, pickup_address.timezone);
              date.setHours(8);
              date.setMinutes(0);
              date = fromZonedTime(date, pickup_address.timezone);
            }

            const { due_date } = estimateDueDate({
              route,
              delivery_address,
              pickup_date: date
            });

            setValues((values) => ({
              ...values,
              due_date
            }));

            setIsLoading(false);

            return;
          }

          const miles = route.distance.miles;
          const days = estimateLTLDays(miles);
          const due_date = calculateDueDate(pickup_date, days);

          setValues((values) => ({
            ...values,
            due_date: formatDate(due_date, 'dateOnly')
          }));

          setIsLoading(false);

          return;
        }

        const due_date = calculateDueDate(
          pickup_date,
          mode === MODE.FTL ? 2 : 5
        );

        setValues((values) => ({
          ...values,
          due_date: formatDate(due_date, 'dateOnly')
        }));

        setIsLoading(false);
      } catch (error) {
        const due_date = calculateDueDate(
          pickup_date,
          mode === MODE.FTL ? 2 : 5
        );

        setValues((values) => ({
          ...values,
          due_date: formatDate(due_date, 'dateOnly')
        }));

        setIsLoading(false);
      }
    };

    estimateDate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    setValues,
    pickup_date,
    mode,
    truck_type,
    pickup_address?.longitude,
    pickup_address?.latitude,
    delivery_address?.longitude,
    delivery_address?.latitude
  ]);

  const handleChange = (date) => {
    if (!date) {
      setValues((values) => ({
        ...values,
        pickup_date: undefined,
        due_date: undefined
      }));
      return;
    }
    setValues((values) => ({
      ...values,
      pickup_date: formatDate(date, 'dateOnly')
    }));
  };

  return (
    <Calendar
      value={value}
      className="w-100 rounded quote-calendar"
      minDate={new Date()}
      maxDate={addDays(new Date(), 30)}
      minDetail="month"
      onChange={handleChange}
      tileClassName={({ date }) => {
        const dateString = formatDate(date, 'dateOnly');

        if (pickup_date === dateString) {
          if (isLoading) {
            return 'is-loading is-loading-white';
          }
          return 'pt-3';
        }

        if (pickup_date && due_date) {
          if (date >= parseDate(pickup_date) && date <= parseDate(due_date)) {
            return 'react-calendar__tile react-calendar__tile--active react-calendar__tile--range react-calendar__month-view__days__day';
          }
          if (dateString === due_date) {
            return 'react-calendar__tile react-calendar__tile--range-end react-calendar__month-view__days__day';
          }
        }
      }}
      tileContent={({ date }) => {
        const dateString = formatDate(date, 'dateOnly');
        if (pickup_date === dateString) {
          if (isLoading) {
            return null;
          }

          return (
            <div
              className="mb-n3 text-truncate animated fadeIn faster"
              style={{ fontSize: '0.75rem' }}
            >
              Pickup
            </div>
          );
        }
        if (due_date === dateString) {
          return (
            <div
              className="mb-n3 text-truncate animated fadeIn faster"
              style={{ fontSize: '0.75rem' }}
            >
              Delivery
            </div>
          );
        }
      }}
      tileDisabled={({ date }) => {
        if (isLTL(mode) && isWeekend(date)) {
          return true;
        }
        if (isLTL(mode) && formatDate(date, 'dateOnly') === pickup_date) {
          return true;
        }
        return false;
      }}
    />
  );
};

const QuoteCalendar = memo(
  ({ setValues, values }) => {
    const { pickup_date, due_date, mode } = values;

    let value = null;
    if (pickup_date && !due_date) {
      value = [parseDate(pickup_date)];
    }
    if (pickup_date && due_date) {
      value = [parseDate(pickup_date), parseDate(due_date)];
    }

    const handleChange = (dates) => {
      if (!dates || !dates.length) {
        setValues((values) => ({
          ...values,
          pickup_date: undefined,
          due_date: undefined
        }));
        return;
      }
      if (dates.length === 1) {
        setValues((values) => ({
          ...values,
          due_date: undefined,
          pickup_date: formatDate(dates[0], 'dateOnly')
        }));
        return;
      }
      setValues((values) => ({
        ...values,
        pickup_date: formatDate(dates[0], 'dateOnly'),
        due_date: formatDate(dates[1], 'dateOnly')
      }));
    };

    return (
      <Calendar
        className="w-100 rounded quote-calendar"
        minDate={new Date()}
        maxDate={addDays(new Date(), 30)}
        minDetail="month"
        allowPartialRange={true}
        onChange={handleChange}
        tileClassName={({ date }) => {
          const dateString = formatDate(date, 'dateOnly');
          if (pickup_date === dateString || due_date === dateString) {
            return 'pt-3';
          }
        }}
        tileContent={({ date }) => {
          const dateString = formatDate(date, 'dateOnly');
          if (pickup_date === dateString && due_date === dateString) {
            return (
              <div
                className="mb-n3 text-truncate animated fadeIn"
                style={{ fontSize: '0.75rem' }}
              >
                Same Day
              </div>
            );
          }
          if (pickup_date === dateString) {
            return (
              <div
                className="mb-n3 text-truncate animated fadeIn faster"
                style={{ fontSize: '0.75rem' }}
              >
                Pickup
              </div>
            );
          }
          if (due_date === dateString) {
            return (
              <div
                className="mb-n3 text-truncate animated fadeIn faster"
                style={{ fontSize: '0.75rem' }}
              >
                Delivery
              </div>
            );
          }
        }}
        tileDisabled={({ date }) => {
          if (isLTL(mode) && isWeekend(date)) {
            return true;
          }
          if (
            isLTL(mode) &&
            formatDate(date, 'dateOnly') === values.pickup_date
          ) {
            return true;
          }
          return false;
        }}
        value={value}
        selectRange={true}
      />
    );
  },
  (prevProps, nextProps) => {
    const prevVals = prevProps.values;
    const nextVals = nextProps.values;

    return (
      prevVals.pickup_date === nextVals.pickup_date &&
      prevVals.due_date === nextVals.due_date &&
      prevVals.mode === nextVals.mode
    );
  }
);

const hasAllValues = ({ units, total_weight, length, width, height }) => {
  return !!(units && total_weight && length && width && height);
};

const freightClassOptions = FREIGHT_CLASSES.map((cls) => {
  return { value: cls, label: cls };
});

const updateLoadWeight = (updated) => {
  updated.load_weight = updated.line_items.reduce((accum, item) => {
    return accum + Number(item.total_weight || 0);
  }, 0);
};

const LineItem = ({ setValues, disabled, index, values }) => {
  const {
    state: { shipper }
  } = useAppState();
  const lineItem = values.line_items[index];
  const [manual, setManual] = useState(false);
  const PACKAGES = isParcel(values) ? PARCEL_PACKAGE_TYPES : PACKAGE_TYPES;

  const removeItem = useCallback(
    (index) => {
      setValues((values) => {
        const updated = delIn(values, `line_items.${index}`);
        updateLoadWeight(updated);
        return updated;
      });
    },
    [setValues]
  );

  const duplicateItem = useCallback(
    (index) => {
      setValues((values) => {
        const item = getIn(values, `line_items.${index}`);
        const updated = pushIn(values, `line_items`, item);
        updateLoadWeight(updated);
        return updated;
      });
    },
    [setValues]
  );

  const handleChange = useCallback(
    ({ target: { name, value } }) => {
      return setValues((values) => {
        const [, index] = name.split('.');
        const updated = setIn(values, name, value);
        if (name.includes('total_weight')) {
          updateLoadWeight(updated);
        }

        if (manual) {
          return updated;
        }

        const line_item = getIn(updated, `line_items.${index}`);

        if (!hasAllValues(line_item)) {
          return updated;
        }

        const freight_class = getFreightClass(line_item);

        return setIn(
          updated,
          `line_items.${index}.freight_class`,
          freight_class
        );
      });
    },
    [setValues, manual]
  );

  const setIsManual = useCallback(() => {
    if (manual) {
      if (hasAllValues(lineItem)) {
        const freight_class = getFreightClass(lineItem);
        setValues((values) =>
          setIn(values, `line_items.${index}.freight_class`, freight_class)
        );
      }
      return setManual(false);
    }

    return setManual(true);
  }, [manual, setValues, index, lineItem]);

  let typeLabel = PACKAGES.find(({ value }) => value === lineItem.type)?.label;
  if (typeLabel) {
    if (typeLabel[typeLabel.length - 1] === 'x') {
      typeLabel += 'es';
    } else if (typeLabel[typeLabel.length - 1] !== 's') {
      typeLabel += 's';
    }
  }

  return (
    <ListGroupItem className="position-relative py-5">
      <Row>
        <Col sm="8" xl="4">
          <FormGroup className="mb-3">
            <Label className="mb-1 small text-secondary">
              Units {typeLabel ? `(Number of ${typeLabel})` : null}
            </Label>
            <InputGroup className="input-group-merge">
              <FormInput
                name={`line_items.${index}.units`}
                type="number"
                onChange={handleChange}
                disabled={disabled}
              />
              <FormInput
                name={`line_items.${index}.type`}
                type="select"
                className="rounded-left-0"
                options={PACKAGES}
                disabled={disabled}
              />
              <FormFeedback name={`line_items.${index}.units`} />
              <FormFeedback name={`line_items.${index}.type`} />
            </InputGroup>
          </FormGroup>
        </Col>
        <Col sm="4" xl="2">
          <FormGroup className="mb-3">
            <Label className="mb-1 small text-secondary">Total Weight</Label>
            <InputGroup className="input-group-merge">
              <FormInput
                className="form-control-appended"
                name={`line_items.${index}.total_weight`}
                type="number"
                onChange={handleChange}
                disabled={disabled}
              />
              <InputGroupAddon addonType="append">
                <InputGroupText>
                  <span className="small">lbs</span>
                </InputGroupText>
              </InputGroupAddon>
              <FormFeedback name={`line_items.${index}.total_weight`} />
              <FormFeedback name="load_weight" detached />
            </InputGroup>
          </FormGroup>
        </Col>
        <Col sm="12" xl={true}>
          <Label className="mb-1 small text-secondary">
            Dimensions (inches)
          </Label>
          <div className="d-flex mb-3">
            <div className="flex-grow-1">
              <InputGroup className="input-group-merge">
                <FormInput
                  className="form-control-appended"
                  name={`line_items.${index}.length`}
                  type="number"
                  onChange={handleChange}
                  disabled={disabled}
                />
                <InputGroupAddon addonType="append">
                  <InputGroupText className="rounded-0">
                    <span className="small">
                      L<span className="d-none d-lg-inline">ength</span>
                    </span>
                  </InputGroupText>
                </InputGroupAddon>
              </InputGroup>
            </div>
            <div className="flex-grow-1">
              <InputGroup className="input-group-merge">
                <FormInput
                  className="form-control-appended rounded-0 border-left-0"
                  name={`line_items.${index}.width`}
                  type="number"
                  onChange={handleChange}
                  disabled={disabled}
                />
                <InputGroupAddon addonType="append">
                  <InputGroupText className="rounded-0">
                    <span className="small">
                      W<span className="d-none d-lg-inline">idth</span>
                    </span>
                  </InputGroupText>
                </InputGroupAddon>
              </InputGroup>
            </div>
            <div className="flex-grow-1">
              <InputGroup className="input-group-merge">
                <FormInput
                  className="form-control-appended rounded-0 border-left-0"
                  name={`line_items.${index}.height`}
                  type="number"
                  onChange={handleChange}
                  disabled={disabled}
                />
                <InputGroupAddon addonType="append">
                  <InputGroupText className="rounded-0">
                    <span className="small">
                      H<span className="d-none d-lg-inline">eight</span>
                    </span>
                  </InputGroupText>
                </InputGroupAddon>
              </InputGroup>
            </div>
            <div className="flex-grow-1">
              <InputGroup className="input-group-merge">
                <FormInput
                  className="form-control-appended rounded-0 border-left-0"
                  name={`line_items.${index}.stack_limit`}
                  type="number"
                  onChange={handleChange}
                  disabled={disabled}
                />
                <InputGroupAddon addonType="append">
                  <InputGroupText>
                    <span className="small">
                      S<span className="d-none d-lg-inline">tack</span>
                    </span>
                  </InputGroupText>
                </InputGroupAddon>
              </InputGroup>
            </div>
          </div>
          <div className="d-flex mt-n3 mb-3">
            <FormFeedback
              name={`line_items.${index}.length`}
              className="mr-3"
              detached
            />
            <FormFeedback
              name={`line_items.${index}.width`}
              className="mr-3"
              detached
            />
            <FormFeedback
              name={`line_items.${index}.height`}
              className="mr-3"
              detached
            />
            <FormFeedback
              name={`line_items.${index}.stack_limit`}
              className="mr-3"
              detached
            />
          </div>
        </Col>
      </Row>
      <Row>
        <Col lg="8">
          <FormGroup className="mb-3">
            <Label className="mb-1 small text-secondary">Description</Label>
            <FormInput
              name={`line_items.${index}.description`}
              className="mr-3 d-inline-block"
              disabled={disabled}
            />
            <FormFeedback name={`line_items.${index}.description`} />
          </FormGroup>
        </Col>
        <Col lg={true}>
          <FormGroup className="mb-3">
            <div className="d-flex align-items-center justify-content-between">
              <Label className="mb-1 small text-secondary">Freight Class</Label>
              <Button
                type="button"
                color="link"
                size="sm"
                className="py-0"
                style={{ padding: 0 }}
                onClick={() => setIsManual()}
              >
                {manual ? 'Estimate' : 'Set Class'}
              </Button>
            </div>
            <FormInput
              name={`line_items.${index}.freight_class`}
              type="select"
              className="mr-3 d-inline-block"
              options={freightClassOptions}
              disabled={disabled || !manual}
            />
            <FormFeedback name={`line_items.${index}.freight_class`} />
          </FormGroup>
        </Col>
      </Row>
      <FormGroup className="mb-3">
        <Label className="mb-1 small text-secondary">Insurance</Label>
        <Input
          checked={!!lineItem.insurance}
          id={`insurance-switch-${index}`}
          type="switch"
          disabled={disabled || !shipper.address}
          onChange={() => {
            if (lineItem.insurance) {
              return setValues((values) => {
                return setIn(
                  values,
                  `line_items.${index}.insurance`,
                  undefined
                );
              });
            }
            setValues((values) => {
              return setIn(values, `line_items.${index}.insurance`, {
                amount: undefined,
                commodity: undefined
              });
            });
          }}
          label={
            isParcel(values) ? (
              <span>
                Add insurance from{' '}
                <a
                  target="_blank"
                  href="https://www.shipengine.com/shipsurance-tos"
                  rel="noreferrer"
                >
                  Shipengine
                </a>
                .
              </span>
            ) : (
              <span>
                Add insurance from{' '}
                <a
                  target="_blank"
                  href="https://www.loadsure.net"
                  rel="noreferrer"
                >
                  Loadsure
                </a>
                . Straightline partners with Loadsure to provide instant,
                no-obligation insurance quotes. Loadsure's dynamic rating engine
                analyzes high-resolution data in real time, creating an accurate
                risk score for every shipment.
              </span>
            )
          }
        />
        {!shipper.address && (
          <div className="mt-2">
            You must update your billing address before adding insurance. Please
            visit the <Link to="/settings">settings page</Link>.
          </div>
        )}
      </FormGroup>
      {lineItem.insurance && (
        <Row className="mb-n3">
          <Col lg="6">
            <FormGroup className="mb-3">
              <Label className="mb-1 small text-secondary">Commodity</Label>
              <FormInput
                type="select"
                // menuIsOpen={true}
                options={commodityOptions}
                name={`line_items.${index}.insurance.commodity`}
                disabled={disabled}
              />
              <FormFeedback name={`line_items.${index}.insurance.commodity`} />
            </FormGroup>
          </Col>
          <Col lg="3">
            <FormGroup className="mb-3">
              <Label className="mb-1 small text-secondary">Insured Value</Label>
              <InputGroup className="input-group-merge">
                <FormInput
                  type="money"
                  name={`line_items.${index}.insurance.amount`}
                  className="form-control-prepended rounded-right"
                  disabled={disabled}
                />
                <InputGroupAddon addonType="prepend">
                  <InputGroupText>
                    <Icon type="dollar-sign" />
                  </InputGroupText>
                </InputGroupAddon>
              </InputGroup>
              <FormFeedback name={`line_items.${index}.insurance.amount`} />
            </FormGroup>
          </Col>
          <Col lg="3">
            <div className="text-secondary small mt-lg-4 mb-3">
              Rates will be returned with additional charges for insurance.
            </div>
          </Col>
        </Row>
      )}
      <div className="position-absolute" style={{ right: 12, top: 8 }}>
        <Button
          type="button"
          color="white"
          size="sm"
          onClick={() => duplicateItem(index)}
          disabled={disabled}
        >
          <Icon type="repeat" className="mr-2" />
          Duplicate
        </Button>
        {index !== 0 && (
          <Button
            type="button"
            color="white"
            size="sm"
            className="ml-2"
            onClick={() => removeItem(index)}
            disabled={disabled}
          >
            <Icon type="x-circle" className="mr-2" />
            Remove
          </Button>
        )}
      </div>
    </ListGroupItem>
  );
};

const LineItems = ({ setValues, values, disabled }) => {
  const line_items = values.line_items || [];

  const addItem = useCallback(() => {
    setValues((values) =>
      pushIn(values, 'line_items', {
        type: isParcel(values.mode) ? 'BOX' : 'PLT',
        units: undefined,
        length: undefined,
        width: undefined,
        height: undefined,
        total_weight: undefined,
        stack_limit: 1,
        freight_class: undefined,
        description: undefined
      })
    );
  }, [setValues]);

  return (
    <FormGroup>
      <div className="d-flex justify-content-between align-items-center">
        <Label className="mr-3">Line Items</Label>
        <Button
          type="button"
          color="white"
          size="sm"
          className="form-text"
          onClick={addItem}
          disabled={disabled}
        >
          <Icon type="plus-circle" className="mr-2" />
          Add Item
        </Button>
      </div>
      <ListGroup className="mb-4">
        {line_items.map((lineItem, index) => {
          return (
            <LineItem
              setValues={setValues}
              values={values}
              index={index}
              disabled={disabled}
              key={index}
            />
          );
        })}
      </ListGroup>
    </FormGroup>
  );
};

const SaveAddress = ({ address, onComplete, disabled }) => {
  const [open, setOpen] = useState(false);
  const toggle = () => setOpen(false);
  const app = useApp();
  const schema = app.service('api/address-details').schema;
  const { create } = app.service('api/address-details').useCreate();

  const isSavable = useMemo(() => {
    return address ? addressSchema.isValidSync(address) : false;
  }, [address]);

  const { result: found, isLoading } = app
    .service('api/address-details')
    .useFindOne(
      {
        query: {
          address: {
            address_1: address?.address_1,
            address_2: address?.address_2,
            city: address?.city,
            state: address?.state,
            postal_code: address?.postal_code,
            country: address?.country
          }
        }
      },
      { enabled: isSavable }
    );

  const onSubmit = (data, formik) => {
    create(data)
      .then((addressDetail) => {
        formik.setSubmitting(false);
        toast.success('Address saved!');
        app.service('api/address-details').invalidateFind();
        toggle();
        onComplete(addressDetail);
      })
      .catch((err) => {
        toast.errors(err);
        if (err.errors) {
          formik.setErrors(err.errors);
        }
        formik.setSubmitting(false);
      });
  };

  if (!isSavable) {
    return null;
  }

  return (
    <React.Fragment>
      {!isLoading && !found && (
        <div className="animated fadeIn fast">
          <Button
            onClick={() => setOpen(true)}
            tabIndex={-1}
            style={{ height: 25, lineHeight: 1 }}
            size="sm"
            // color="white"
            // className="border-0 form-text text-muted"
            color="primary"
            disabled={disabled}
          >
            Save Address
          </Button>
        </div>
      )}
      <Modal isOpen={open} toggle={toggle} scrollable>
        {() => {
          return (
            <Formik
              onSubmit={onSubmit}
              initialValues={{
                name: address.name || undefined,
                address: {
                  ...address,
                  name: address.name || undefined,
                  contact: undefined,
                  phone: undefined,
                  email: undefined,
                  appointment: undefined
                },
                description: undefined,
                default_address: undefined
              }}
              validationSchema={schema}
              enableReinitialize
              render={({ isSubmitting, values, setValues }) => {
                return (
                  <Form className="modal-content">
                    <ModalBody>
                      <div className="mx-n4 mt-n4">
                        <MapImage
                          className="mw-100 rounded-top"
                          size="600x150"
                          apiKey={process.env.REACT_APP_GOOGLE_MAPS_KEY}
                        >
                          <MarkerImage
                            location={{
                              lat: address.latitude,
                              lng: address.longitude
                            }}
                            color="red"
                          />
                        </MapImage>
                      </div>
                      <div className="my-4">
                        Save this address and its corresponding details to be
                        used for future quotes. You can view and edit your saved
                        address on the <Link to="/addresses">Addresses</Link>{' '}
                        page.
                      </div>
                      <FormGroup>
                        <Label>Address Name*</Label>
                        <FormInput
                          name="name"
                          disabled={isSubmitting}
                          placeholder="Enter your name for this address"
                        />
                        <FormFeedback name="name" />
                        <div className="text-muted mt-2">
                          This is a nickname for this address. It is used when
                          searching for saved address but is not shown on the
                          shipment or to carriers.
                        </div>
                      </FormGroup>
                      <FormGroup>
                        <Label>Address*</Label>
                        <FormInput
                          useAddress2
                          type="address"
                          name="address"
                          disabled={isSubmitting}
                          placeholder="Enter an address"
                          invalid={false}
                          initialValue={values.address}
                          addressToOption={(address) => {
                            if (!address || !address.address_1) {
                              return undefined;
                            }
                            return {
                              label: `${address.address_1}, ${address.city}, ${address.state}, ${address.postal_code}`,
                              value: address
                            };
                          }}
                          onChange={({ target: { value } }) => {
                            if (!value) {
                              return setValues({
                                ...values,
                                address: null
                              });
                            }

                            setValues({
                              ...values,
                              address: {
                                ...values.address,
                                ...value,
                                name:
                                  values.address?.name ||
                                  value.name ||
                                  undefined
                              }
                            });
                          }}
                        />
                        <FormFeedback name="address" />
                      </FormGroup>
                      <FormGroup>
                        <Label>Company Name*</Label>
                        <FormInput
                          name="address.name"
                          disabled={isSubmitting}
                          placeholder="Enter Company or Location name"
                        />
                        <FormFeedback name="address.name" />
                      </FormGroup>
                      <FormGroup>
                        <Label>Contact Name</Label>
                        <FormInput
                          name="address.contact"
                          disabled={isSubmitting}
                          placeholder="Enter Contact Name"
                        />
                        <FormFeedback name="address.contact" />
                      </FormGroup>
                      <FormGroup>
                        <Label>Contact Phone</Label>
                        <FormInput
                          name="address.phone"
                          type="phone"
                          disabled={isSubmitting}
                          placeholder="Enter Contact Phone"
                        />
                        <FormFeedback name="address.phone" />
                      </FormGroup>
                      <FormGroup>
                        <Label>Contact Email</Label>
                        <FormInput
                          name="address.email"
                          disabled={isSubmitting}
                          placeholder="Enter Contact Email"
                        />
                        <FormFeedback name="address.email" />
                      </FormGroup>
                      <FormGroup>
                        <Label>Description</Label>
                        <FormInput
                          name="description"
                          type="textarea"
                          rows="4"
                          disabled={isSubmitting}
                          placeholder="Enter details or notes about the address"
                        />
                        <FormFeedback name="description" />
                      </FormGroup>
                      <FormGroup>
                        <Label>Default Appointment</Label>
                        <FormInput
                          name="address.appointment"
                          rows="4"
                          disabled={isSubmitting}
                          placeholder="Enter a default appointment"
                        />
                        <div className="text-secondary small mt-2">
                          Default appointment will automatically be applied to
                          new quotes.
                        </div>
                        <FormFeedback name="address.appointment" />
                      </FormGroup>
                      <FormGroup>
                        <Label>Default Address</Label>
                        <FormInput
                          name="default_address"
                          type="radio-buttons"
                          disabled={isSubmitting}
                          emptyValue={null}
                          options={[
                            { label: 'Pickup', value: 'pickup' },
                            { label: 'Delivery', value: 'delivery' }
                          ]}
                          onChange={({ target: { value } }) => {
                            setValues({
                              ...values,
                              default_address:
                                value === values.default_address ? null : value
                            });
                          }}
                        />
                        <div className="text-secondary small mt-2">
                          Select if this address is your regular pickup or
                          delivery.
                        </div>
                      </FormGroup>
                    </ModalBody>
                    <ModalFooter>
                      <div className="d-sm-flex justify-content-between w-100 m-0">
                        <div>
                          <Button
                            color="white"
                            type="button"
                            className="btn-xs-block mb-3 mb-sm-0"
                            onClick={toggle}
                            disabled={isSubmitting}
                          >
                            Close
                          </Button>
                        </div>
                        <div className="text-right">
                          <Button
                            type="submit"
                            color="primary"
                            className={`btn-xs-block mb-3 mb-sm-0 px-5 ${
                              isSubmitting ? 'is-loading' : ''
                            }`}
                            disabled={isSubmitting}
                          >
                            Save Address
                          </Button>
                        </div>
                      </div>
                    </ModalFooter>
                  </Form>
                );
              }}
            />
          );
        }}
      </Modal>
    </React.Fragment>
  );
};
